import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// ─── Una pestaña abierta no se queda con la versión vieja ───────────────────
//
// Hasta el 2026-10-10 la web no se enteraba de un deploy: una pestaña abierta
// seguía corriendo el código con el que se cargó, durante días. Medido ese día:
// ~10 navegadores seguían llamando a funciones que el web había dejado de usar
// el 6-oct (la campana de notificaciones, cada pocos minutos). Eso impide
// borrar las funciones viejas del servidor, y después de cada deploy deja
// gente con una pantalla que puede llamar a algo que ya no existe.
//
// Cómo sabe la versión: Vite nombra el archivo de entrada con un hash de su
// contenido (`/assets/index-<hash>.js`). El `index.html` desplegado apunta al
// de la versión NUEVA; el que corre esta pestaña es el de la suya.
//
// Cuándo recarga:
//   · al CAMBIAR DE PANTALLA después de detectar una versión nueva — nunca en
//     medio de una pantalla, donde se perdería lo que se está escribiendo;
//   · enseguida si falla la carga de un archivo de la versión vieja
//     (`vite:preloadError`): la pantalla ya está rota, recargar la arregla.

const ENTRY = /\/assets\/index-[\w-]+\.js/;
const CHECK_EVERY_MS = 5 * 60 * 1000;
/** Contra un bucle: si la versión nueva TAMBIÉN falla, no recargar sin fin. */
const RELOAD_GUARD_KEY = 'version-check:last-reload';
const RELOAD_GUARD_MS = 60 * 1000;

/** El archivo de entrada que referencia un `index.html`, o `null`. */
export function entryOf(html: string): string | null {
  return html.match(ENTRY)?.[0] ?? null;
}

/** Hay versión nueva solo si se conocen las DOS y son distintas: una lectura
 *  fallida o un HTML inesperado nunca provocan una recarga. */
export function isNewVersion(running: string | null, deployed: string | null): boolean {
  return !!running && !!deployed && running !== deployed;
}

function runningEntry(): string | null {
  for (const s of Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    const m = entryOf(s.getAttribute('src') ?? '');
    if (m) return m;
  }
  return null;
}

async function deployedEntry(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    return res.ok ? entryOf(await res.text()) : null;
  } catch {
    return null; // sin red: se vuelve a intentar en la próxima vuelta
  }
}

function reloadOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sin sessionStorage: se recarga igual (el peor caso es una recarga de más)
  }
  window.location.reload();
}

/** Montar UNA vez, dentro del router. No hace nada en desarrollo. */
export function useReloadOnNewVersion(): void {
  const location = useLocation();
  const pending = useRef(false);
  const first = useRef(true);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    const running = runningEntry();
    if (!running) return; // sin entrada reconocible no hay con qué comparar

    const check = async () => {
      if (pending.current) return;
      if (isNewVersion(running, await deployedEntry())) pending.current = true;
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onPreloadError = (e: Event) => {
      e.preventDefault(); // el error ya no sube: lo resuelve la recarga
      reloadOnce();
    };

    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, []);

  // Al cambiar de pantalla con una versión nueva esperando: la URL ya es la
  // nueva, así que recargar abre ESA pantalla con el código nuevo.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (pending.current) reloadOnce();
  }, [location.pathname]);
}
